import { FC } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Colors } from '../../styles/theme';
import { PricingPlan } from './index';
import { APP_CONFIG } from '../../const/APP_CONFIG';

interface PricingCardProps {
  plan: PricingPlan;
  isYearly: boolean;
}

const formatPrice = (price: number) => price.toLocaleString();

const PricingCard: FC<PricingCardProps> = ({ plan, isYearly }) => {
  // 항상 월 가격 표시 (yearly 선택 시에도 월 단위)
  const currentPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const originalPrice = isYearly
    ? plan.originalYearlyPrice
    : plan.originalMonthly;
  const yearlyTotal = plan.yearlyPrice ? plan.yearlyPrice * 12 : null;
  const isCustomPrice = currentPrice === null;
  const hasDiscount =
    originalPrice !== undefined && originalPrice > (currentPrice ?? 0);

  // 정가 대비 할인율 (originalPrice 옆에 표시)
  const originalDiscountPercent =
    originalPrice && currentPrice
      ? Math.round((1 - currentPrice / originalPrice) * 100)
      : 0;

  // 월간 대비 연간 할인율 (yearly 선택 시 가격 옆에 표시)
  const yearlyDiscountPercent =
    isYearly && plan.monthlyPrice && plan.yearlyPrice
      ? Math.round((1 - plan.yearlyPrice / plan.monthlyPrice) * 100)
      : 0;

  const handleClick = () => {
    const billingCycle = isYearly ? 'Yearly' : 'Monthly';
    const priceInfo = isCustomPrice
      ? 'Custom pricing'
      : isYearly && yearlyTotal
      ? `$${formatPrice(currentPrice!)}/mo ($${formatPrice(yearlyTotal)}/yr)`
      : `$${formatPrice(currentPrice!)}/mo`;
    const subject = encodeURIComponent(
      `[${APP_CONFIG.name}] ${plan.name} Plan Inquiry`,
    );
    const body = encodeURIComponent(
      `Hi,\n\nI'm interested in the ${plan.name} plan.\n\nPlan: ${plan.name}\nBilling: ${billingCycle}\nPrice: ${priceInfo}\n\nPlease let me know the next steps.\n\nThank you.`,
    );
    const ccList = APP_CONFIG.contact.cc?.filter(Boolean).join(',');
    const ccParam = ccList ? `&cc=${ccList}` : '';
    window.location.href = `mailto:${APP_CONFIG.contact.email}?subject=${subject}&body=${body}${ccParam}`;
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: plan.highlighted ? '#FFFFFF' : '#F5F5F5',
        borderRadius: '24px',
        px: { xs: 3, md: 4 },
        py: { xs: 3, md: 4 },
        boxShadow: plan.highlighted
          ? '0px 4px 20px rgba(0, 0, 0, 0.08)'
          : 'none',
        border: plan.highlighted ? `2px solid ${Colors.blue}` : 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Plan Name */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, height: '24px' }}
      >
        <Typography
          sx={{
            fontSize: { xs: '14px', md: '16px' },
            fontWeight: 600,
            color: Colors.blue,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          {plan.name}
        </Typography>
        {/* {yearlyDiscountPercent > 0 && (
          <Box
            sx={{
              backgroundColor: '#E8F4FF',
              color: Colors.blue,
              fontSize: { xs: '12px', md: '14px' },
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: '4px',
            }}
          >
            -{yearlyDiscountPercent}%
          </Box>
        )} */}
      </Stack>

      {/* Description */}
      {plan.description && (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#9E9E9E',
            mb: 2,
          }}
        >
          {plan.description}
        </Typography>
      )}

      {/* Price */}
      <Box sx={{ mb: { xs: 1, md: 2 } }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ height: { xs: '40px', md: '58px' } }}
        >
          <Typography
            sx={{
              fontSize: { xs: '34px', md: '48px' },
              fontWeight: 700,
              color: hasDiscount ? Colors.blue : '#38383B',
              lineHeight: 1,
              letterSpacing: '-0.5px',
            }}
          >
            {isCustomPrice
              ? plan.customPriceLabel
              : `$${formatPrice(currentPrice!)}`}
          </Typography>
          {hasDiscount && (
            <>
              <Typography
                sx={{
                  fontSize: { xs: '34px', md: '48px' },
                  fontWeight: 300,
                  color: '#C0C0C0',
                  textDecoration: 'line-through',
                  textDecorationThickness: '2px',
                  letterSpacing: '-0.5px',
                  lineHeight: 1,
                }}
              >
                ${formatPrice(originalPrice)}
              </Typography>
              {originalDiscountPercent > 0 && (
                <Box
                  sx={{
                    backgroundColor: '#E8F4FF',
                    color: Colors.blue,
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 600,
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                  }}
                >
                  -{originalDiscountPercent}%
                </Box>
              )}
            </>
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: { xs: '11px', md: '14px' },
            fontWeight: 400,
            color: '#9E9E9E',
            height: { xs: '18px', md: '22px' },
            mt: { xs: 0, md: 0.5 },
          }}
        >
          {isYearly && yearlyTotal
            ? `per month, $${formatPrice(yearlyTotal)} billed annually`
            : !isCustomPrice
            ? 'per month'
            : ''}
        </Typography>
      </Box>

      {/* Features */}
      <Stack spacing={2} sx={{ mb: 4, flex: 1 }}>
        {plan.features.map((feature, index) => (
          <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#E8F4FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CheckIcon sx={{ fontSize: '14px', color: Colors.blue }} />
            </Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#38383B',
              }}
            >
              {feature}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* CTA Button */}
      <Button
        variant={plan.highlighted ? 'contained' : 'outlined'}
        fullWidth
        onClick={handleClick}
        sx={{
          backgroundColor: plan.highlighted ? Colors.blue : 'transparent',
          color: plan.highlighted ? '#FFFFFF' : Colors.blue,
          borderColor: Colors.blue,
          py: { xs: 1, md: 1.5 },
          borderRadius: '12px',
          fontWeight: 700,
          fontSize: '16px',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: plan.highlighted ? '#0056CC' : '#E8F4FF',
            borderColor: Colors.blue,
          },
        }}
      >
        {plan.buttonText || 'Get Started'}
      </Button>
    </Box>
  );
};

export default PricingCard;
